import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiSearch, FiRefreshCw, FiX, FiEye, FiPrinter } from 'react-icons/fi';
import { FaExclamationTriangle } from 'react-icons/fa';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import { useImpresion } from '../../contexts/ContextoImpresion';
import SpinnerCargando from '../util/SpinnerCargando';
import PlantillaModal from '../util/PlantillaModal';

export default function HistorialCierres() {
  const { tienePermiso } = useContextoSesion();
  const { imprimirDocumento } = useImpresion();
  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [cajas, setCajas] = useState([]);
  const [filtros, setFiltros] = useState({ fecha_desde: '', fecha_hasta: '', id_cuenta_tesoreria: '' });
  const [detalleSeleccionado, setDetalleSeleccionado] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [modalDetalleAbierto, setModalDetalleAbierto] = useState(false);

  // Cierre forzado
  const [modalCierreForzado, setModalCierreForzado] = useState(null); // sesion object or null
  const [comentarioCierreForzado, setComentarioCierreForzado] = useState('');
  const [cerrandoForzado, setCerrandoForzado] = useState(false);

  useEffect(() => {
    cargarCajas();
    cargarHistorial();
  }, []);

  const cargarCajas = async () => {
    try {
      const response = await instanciaAxios.get('/sesion-caja/cajas-disponibles');
      setCajas(response.data);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar cajas:', error);
    }
  };

  const cargarHistorial = async () => {
    try {
      setCargando(true);
      const params = new URLSearchParams();
      if (filtros.fecha_desde) params.append('fecha_desde', filtros.fecha_desde);
      if (filtros.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta);
      if (filtros.id_cuenta_tesoreria) params.append('id_cuenta_tesoreria', filtros.id_cuenta_tesoreria);
      
      const response = await instanciaAxios.get(`/sesion-caja/historial?${params.toString()}`);
      setHistorial(response.data);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar historial:', error);
      toast.error('Error al cargar el historial');
    } finally {
      setCargando(false);
    }
  };

  const verDetalle = async (sesion) => {
    // Verificar si la caja está cerrada
    if (sesion.estado !== 'Cerrada') {
      toast.warning('El cuadre no está disponible aún. La caja debe estar cerrada para ver el detalle.');
      return;
    }

    try {
      setCargandoDetalle(true);
      setModalDetalleAbierto(true);
      const response = await instanciaAxios.get(`/sesion-caja/${sesion.id_sesion_caja}/detalle`);
      setDetalleSeleccionado(response.data);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar detalle:', error);
      toast.error('Error al cargar el detalle del cierre');
      setModalDetalleAbierto(false);
    } finally {
      setCargandoDetalle(false);
    }
  };

  const ejecutarCierreForzado = async () => {
    if (!modalCierreForzado?.id_sesion_caja) return;
    if (!comentarioCierreForzado.trim()) {
      toast.warning('Debes ingresar una razón para el cierre forzado');
      return;
    }

    try {
      setCerrandoForzado(true);
      await instanciaAxios.post(`/sesion-caja/${modalCierreForzado.id_sesion_caja}/cierre-forzado`, {
        comentario_cierre: comentarioCierreForzado.trim()
      });

      toast.success('Caja cerrada forzosamente.');
      setModalCierreForzado(null);
      setComentarioCierreForzado('');
      await cargarHistorial();
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error en cierre forzado:', error);
      toast.error(error.response?.data?.mensaje || 'Error al realizar cierre forzado');
    } finally {
      setCerrandoForzado(false);
    }
  };

  const cerrarModal = () => {
    setModalDetalleAbierto(false);
    setDetalleSeleccionado(null);
  };

  const formatMonto = (monto) => {
    return parseFloat(monto || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-DO', { 
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatFechaHora = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleString('es-DO', { 
      dateStyle: 'short', 
      timeStyle: 'short' 
    });
  };

  const puedeHacerCierreForzado = tienePermiso('caja_cierre_forzado');

  if (cargando && historial.length === 0) {
    return <SpinnerCargando />;
  }

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="flex justify-center py-10">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border border-gray-100 p-12 w-full max-w-7xl mx-10">
          <div className="flex flex-col gap-8">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="font-cascadia font-bold text-2xl">Historial de Cierres</h1>
                <p className="text-gray-500">Consulta cierres anteriores de todas las cajas</p>
              </div>
              <button
                onClick={cargarHistorial}
                disabled={cargando}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                title="Actualizar"
              >
                <FiRefreshCw size={20} className={cargando ? 'animate-spin' : ''} />
              </button>
            </div>

            {/* Filtros */}
            <div className="bg-white rounded-xl border border-gray-300 shadow-md overflow-hidden">
              <div className="p-4 flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Caja</label>
                  <select
                    value={filtros.id_cuenta_tesoreria}
                    onChange={(e) => setFiltros({ ...filtros, id_cuenta_tesoreria: e.target.value })}
                    className="px-4 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors min-w-[200px]"
                  >
                    <option value="">Todas las cajas</option>
                    {cajas.map(caja => (
                      <option key={caja.id_cuenta_tesoreria} value={caja.id_cuenta_tesoreria}>
                        {caja.descripcion}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha Desde</label>
                  <input
                    type="date"
                    value={filtros.fecha_desde}
                    onChange={(e) => setFiltros({ ...filtros, fecha_desde: e.target.value })}
                    className="px-4 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha Hasta</label>
                  <input
                    type="date"
                    value={filtros.fecha_hasta}
                    onChange={(e) => setFiltros({ ...filtros, fecha_hasta: e.target.value })}
                    className="px-4 py-2 border border-gray-400 rounded-xl focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
                  />
                </div>
                <button
                  onClick={cargarHistorial}
                  className="flex items-center gap-2 px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-green-700 transition-colors"
                >
                  <FiSearch size={16} />
                  Buscar
                </button>
                {(filtros.fecha_desde || filtros.fecha_hasta || filtros.id_cuenta_tesoreria) && (
                  <button
                    onClick={() => {
                      setFiltros({ fecha_desde: '', fecha_hasta: '', id_cuenta_tesoreria: '' });
                      setTimeout(cargarHistorial, 0);
                    }}
                    className="flex items-center gap-1 px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  >
                    <FiX size={16} />
                    Limpiar
                  </button>
                )}
              </div>
            </div>

            {/* Tabla */}
            <div className="bg-white rounded-xl border border-gray-300 shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-color-primario text-white">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Caja</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Usuario</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Apertura</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">Cierre</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Esperado</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Real</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider">Diferencia</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Estado</th>
                      <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {historial.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-12 text-center">
                          <p className="text-gray-500">No se encontraron cierres</p>
                        </td>
                      </tr>
                    ) : (
                      historial.map((item) => (
                        <tr key={item.id_sesion_caja} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3">
                            <span className="font-medium text-gray-900">{item.nombre_caja}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{item.nombre_usuario}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatFecha(item.fecha_apertura)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatFecha(item.fecha_cierre)}</td>
                          <td className="px-4 py-3 text-right text-sm text-gray-600">
                            RD$ {formatMonto(item.monto_esperado)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-600">
                            RD$ {formatMonto(item.monto_real)}
                          </td>
                          <td className={`px-4 py-3 text-right font-semibold ${
                            parseFloat(item.diferencia || 0) === 0 ? 'text-green-600' :
                            parseFloat(item.diferencia || 0) > 0 ? 'text-blue-600' : 'text-red-600'
                          }`}>
                            RD$ {formatMonto(item.diferencia)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              item.estado === 'Cerrada' ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {item.estado}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              {item.estado === 'Cerrada' && (
                                <>
                                  <button
                                    onClick={() => verDetalle(item)}
                                    className="px-3 py-1.5 bg-color-primario text-white text-sm font-medium rounded-xl hover:bg-opacity-90 transition-colors inline-flex items-center gap-1"
                                  >
                                    <FiEye size={14} />
                                    Ver
                                  </button>
                                  <button
                                    onClick={() => imprimirDocumento({
                                      idDocumento: item.id_sesion_caja,
                                      tipoDocumento: 'cierre_caja',
                                      formato: 'continuo'
                                    })}
                                    className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors inline-flex items-center gap-1"
                                  >
                                    <FiPrinter size={14} />
                                    Imprimir
                                  </button>
                                </>
                              )}
                              {item.estado === 'Abierta' && puedeHacerCierreForzado && (
                                <button
                                  onClick={() => {
                                    setModalCierreForzado(item);
                                    setComentarioCierreForzado('');
                                  }}
                                  className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700 transition-colors inline-flex items-center gap-1"
                                >
                                  <FaExclamationTriangle size={12} />
                                  Cierre Forzado
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de detalle */}
      <PlantillaModal
        mostrar={modalDetalleAbierto}
        onClose={cerrarModal}
        titulo={`Detalle del Cierre - ${detalleSeleccionado?.sesion?.nombre_caja || ''}`}
        ancho="max-w-4xl"
      >
        {cargandoDetalle ? (
          <SpinnerCargando />
        ) : detalleSeleccionado ? (
          <div className="space-y-6">
            {/* Info general */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-3 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Usuario</p>
                <p className="font-semibold text-gray-900">{detalleSeleccionado.sesion?.nombre_usuario}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Apertura</p>
                <p className="font-semibold text-gray-900">{formatFechaHora(detalleSeleccionado.sesion?.fecha_apertura)}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Cierre</p>
                <p className="font-semibold text-gray-900">{formatFechaHora(detalleSeleccionado.sesion?.fecha_cierre)}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Fondo Apertura</p>
                <p className="font-semibold text-gray-900">RD$ {formatMonto(detalleSeleccionado.sesion?.monto_apertura)}</p>
              </div>
            </div>

            {/* Desglose de efectivo */}
            {detalleSeleccionado.desglose_efectivo?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-300 p-4">
                <h3 className="font-cascadia text-base font-semibold text-gray-700 mb-3">Desglose de Efectivo</h3>
                <div className="border border-gray-400 rounded overflow-hidden">
                  <table className="w-full text-sm font-mono">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="text-left py-1.5 px-3 border-b border-r border-gray-400 font-semibold text-gray-700">Denominación</th>
                        <th className="text-center py-1.5 px-3 border-b border-r border-gray-400 font-semibold text-gray-700">Cantidad</th>
                        <th className="text-right py-1.5 px-3 border-b border-gray-400 font-semibold text-gray-700">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalleSeleccionado.desglose_efectivo.map((d, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="py-1 px-3 border-b border-r border-gray-300">RD$ {parseFloat(d.denominacion).toLocaleString('es-DO')}</td>
                          <td className="py-1 px-3 border-b border-r border-gray-300 text-center font-medium">{d.cantidad}</td>
                          <td className="py-1 px-3 border-b border-gray-300 text-right font-medium">RD$ {formatMonto(d.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Métodos de pago */}
            {detalleSeleccionado.metodos_pago?.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-300 p-4">
                <h3 className="font-cascadia text-base font-semibold text-gray-700 mb-3">Cuadre por Método de Pago</h3>
                <div className="border border-gray-400 rounded overflow-hidden">
                  <table className="w-full text-sm font-mono">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="text-left py-1.5 px-3 border-b border-r border-gray-400 font-semibold text-gray-700">Método</th>
                        <th className="text-right py-1.5 px-3 border-b border-r border-gray-400 font-semibold text-gray-700">Esperado</th>
                        <th className="text-right py-1.5 px-3 border-b border-r border-gray-400 font-semibold text-gray-700">Real</th>
                        <th className="text-right py-1.5 px-3 border-b border-gray-400 font-semibold text-gray-700">Diferencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detalleSeleccionado.metodos_pago.map((m, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="py-1 px-3 border-b border-r border-gray-300 font-medium">{m.metodo_pago}</td>
                          <td className="py-1 px-3 border-b border-r border-gray-300 text-right">RD$ {formatMonto(m.monto_esperado)}</td>
                          <td className="py-1 px-3 border-b border-r border-gray-300 text-right">RD$ {formatMonto(m.monto_real)}</td>
                          <td className={`py-1 px-3 border-b border-gray-300 text-right font-semibold ${
                            parseFloat(m.diferencia) === 0 ? 'text-green-600' :
                            parseFloat(m.diferencia) > 0 ? 'text-blue-600' : 'text-red-600'
                          }`}>
                            RD$ {formatMonto(m.diferencia)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Comentario */}
            {detalleSeleccionado.sesion?.comentario_cierre && (
              <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                <p className="text-sm font-medium text-yellow-700 mb-1">Comentario:</p>
                <p className="text-sm text-yellow-800">{detalleSeleccionado.sesion.comentario_cierre}</p>
              </div>
            )}
          </div>
        ) : null}
      </PlantillaModal>

      {/* Modal de Cierre Forzado */}
      {modalCierreForzado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4 border border-red-200">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <FaExclamationTriangle className="text-red-600 text-xl" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">Cierre Forzado de Caja</h3>
                <p className="text-sm text-gray-500">Esta acción no se puede deshacer</p>
              </div>
            </div>

            {/* Info de la sesión */}
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-4">
              <p className="text-sm font-medium text-gray-800">
                <span className="font-bold">{modalCierreForzado.nombre_caja}</span>
              </p>
              <p className="text-sm text-red-600 mt-1">
                En uso por: {modalCierreForzado.nombre_usuario}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Abierta desde: {formatFechaHora(modalCierreForzado.fecha_apertura)}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                La caja se cerrará sin cuadre. El comentario quedará registrado con el prefijo [CIERRE FORZADO].
              </p>
            </div>

            {/* Campo de razón (obligatorio) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Razón del cierre forzado <span className="text-red-500">*</span>
              </label>
              <textarea
                value={comentarioCierreForzado}
                onChange={(e) => setComentarioCierreForzado(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows="3"
                placeholder="Ej: El usuario no cerró la caja al final del turno..."
                autoFocus
              />
            </div>

            {/* Botones */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setModalCierreForzado(null);
                  setComentarioCierreForzado('');
                }}
                disabled={cerrandoForzado}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={ejecutarCierreForzado}
                disabled={cerrandoForzado || !comentarioCierreForzado.trim()}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {cerrandoForzado ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Cerrando...
                  </>
                ) : (
                  <>
                    <FaExclamationTriangle className="text-sm" />
                    Confirmar Cierre
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
